import React, { useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import { createPlatformPlan, updatePlatformPlan } from '../../services/api';

const PERIOD_LABELS = { month: 'мес.', year: 'год' };

// ✓ / ✗ для наличия фичи в тарифе — вместо эмодзи-глифов, которые
// по-разному рисуются в разных ОС.
const StatusIcon = ({ ok }) => (
  ok ? (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ marginLeft: 4 }} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 12.5L10.5 14.5L15.5 9.5" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ marginLeft: 4 }} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" d="M14.5 9.50002L9.5 14.5M9.49998 9.5L14.5 14.5" />
    </svg>
  )
);

const emptyPlanForm = {
  name: '', price: '', billing_period: 'month', description: '',
  max_employees: '', has_crm: true, has_kpi: true, overage_price_per_seat: '',
};

const PlansCard = ({ plans, loading, orgs = [], onChanged }) => {
  const [planForm, setPlanForm] = useState(emptyPlanForm);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [planFormError, setPlanFormError] = useState('');

  // Сколько организаций сидит на тарифе — без этого непонятно,
  // что сломается при изменении цены или лимитов.
  const orgsPerPlan = orgs.reduce((acc, org) => {
    if (org.plan_id) acc[org.plan_id] = (acc[org.plan_id] || 0) + 1;
    return acc;
  }, {});

  const handleSavePlan = async (e) => {
    e.preventDefault();
    setPlanFormError('');
    setSaving(true);
    const payload = {
      ...planForm,
      max_employees: planForm.max_employees === '' ? null : planForm.max_employees,
      overage_price_per_seat: planForm.overage_price_per_seat === '' ? null : planForm.overage_price_per_seat,
    };
    try {
      if (editingPlanId) {
        await updatePlatformPlan(editingPlanId, payload);
      } else {
        await createPlatformPlan(payload);
      }
      setPlanForm(emptyPlanForm);
      setEditingPlanId(null);
      await onChanged();
    } catch (err) {
      setPlanFormError(err.response?.data?.error || 'Не удалось сохранить тариф');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name, price: plan.price, billing_period: plan.billing_period,
      description: plan.description || '', max_employees: plan.max_employees ?? '',
      has_crm: plan.has_crm, has_kpi: plan.has_kpi,
      overage_price_per_seat: plan.overage_price_per_seat ?? '',
    });
  };

  const handleCancelEditPlan = () => {
    setEditingPlanId(null);
    setPlanForm(emptyPlanForm);
    setPlanFormError('');
  };

  return (
    <section className="ui-card">
      <div className="ui-card-head">
        <h2>Тарифы</h2>
        <p>Список, который организации видят на своей странице биллинга.</p>
      </div>

      <form className="ui-form" onSubmit={handleSavePlan}>
        <div className="ui-grid">
          <div className="ui-field">
            <label>Название</label>
            <input
              value={planForm.name}
              onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
              placeholder="Базовый"
              required
            />
          </div>
          <div className="ui-field">
            <label>Цена, ₽</label>
            <input
              type="number" min="1" step="1"
              value={planForm.price}
              onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
              placeholder="1990"
              required
            />
          </div>
          <div className="ui-field">
            <label>Период</label>
            <select
              value={planForm.billing_period}
              onChange={(e) => setPlanForm({ ...planForm, billing_period: e.target.value })}
            >
              <option value="month">Месяц</option>
              <option value="year">Год</option>
            </select>
          </div>
          <div className="ui-field">
            <label>Лимит сотрудников (пусто = без лимита)</label>
            <input
              type="number" min="1" step="1"
              value={planForm.max_employees}
              onChange={(e) => setPlanForm({ ...planForm, max_employees: e.target.value })}
              placeholder="без лимита"
            />
          </div>
          <div className="ui-field">
            <label>Доплата за место сверх лимита, ₽/мес (пусто = блокировать)</label>
            <input
              type="number" min="0" step="1"
              value={planForm.overage_price_per_seat}
              onChange={(e) => setPlanForm({ ...planForm, overage_price_per_seat: e.target.value })}
              placeholder="блокировать создание"
            />
          </div>
          <div className="ui-field wide">
            <label>Описание (необязательно)</label>
            <input
              value={planForm.description}
              onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
              placeholder="Что входит в тариф"
            />
          </div>
          <div className="ui-field">
            <label>
              <input
                type="checkbox"
                checked={planForm.has_crm}
                onChange={(e) => setPlanForm({ ...planForm, has_crm: e.target.checked })}
              />
              {' '}CRM
            </label>
          </div>
          <div className="ui-field">
            <label>
              <input
                type="checkbox"
                checked={planForm.has_kpi}
                onChange={(e) => setPlanForm({ ...planForm, has_kpi: e.target.checked })}
              />
              {' '}KPI-аналитика
            </label>
          </div>
        </div>
        {planFormError && <div className="ui-inline-error">{planFormError}</div>}
        <div className="ui-form-actions">
          <button type="submit" className="ui-btn-primary" disabled={saving}>
            {saving ? 'Сохраняем…' : editingPlanId ? 'Сохранить тариф' : 'Создать тариф'}
          </button>
          {editingPlanId && (
            <button type="button" className="ui-btn-ghost" onClick={handleCancelEditPlan}>Отмена</button>
          )}
        </div>
      </form>

      {loading && <LoadingSpinner />}
      {!loading && plans.length === 0 && (
        <div className="ui-empty">Тарифов пока нет</div>
      )}
      {!loading && plans.length > 0 && (
        <div className="platform-org-table">
          {plans.map((plan) => (
            <div className="platform-org-row" key={plan.id}>
              <div className="platform-org-main">
                <div className="platform-org-name">{plan.name}</div>
                {plan.description && <div className="ui-dim">{plan.description}</div>}
              </div>
              <span className="ui-badge">{plan.price} ₽ / {PERIOD_LABELS[plan.billing_period] || plan.billing_period}</span>
              <span className="ui-badge">{plan.max_employees ? `до ${plan.max_employees} сотр.` : 'без лимита'}</span>
              {plan.max_employees && (
                <span className="ui-badge">
                  {plan.overage_price_per_seat ? `+${plan.overage_price_per_seat} ₽/место сверх` : 'блок сверх лимита'}
                </span>
              )}
              <span className="ui-badge">CRM<StatusIcon ok={plan.has_crm} /></span>
              <span className="ui-badge">KPI<StatusIcon ok={plan.has_kpi} /></span>
              <span className="ui-dim">{orgsPerPlan[plan.id] || 0} орг.</span>
              <div className="platform-org-actions">
                <button type="button" className="ui-btn-ghost" onClick={() => handleEditPlan(plan)}>Редактировать</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default PlansCard;
